/*
 * Wettersymbole fuer die Karte (23.10.2026). Eigene Zeichnung im Stil der
 * HVACloud-Icons (24er-Raster, runde Linien), haengen am eigenen Zustand des
 * Servers (WeatherCondition), nicht am Anbieter. Feste Farben auf weissem
 * Kreis, damit sie auf heller und dunkler Karte gleich lesbar sind.
 */

export type WeatherCondition =
  | 'Unknown' | 'Clear' | 'PartlyCloudy' | 'Cloudy' | 'Fog' | 'Drizzle' | 'Rain'
  | 'FreezingRain' | 'Showers' | 'Snow' | 'Thunderstorm';

const SUN = '#f0a92e';
const CLOUD = '#7d8a99';
const WATER = '#2f86d6';
const ICE = '#5fb3e8';

const sun = (cx: number, cy: number, r: number) =>
  `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${SUN}" stroke="none"/>`
  + `<g stroke="${SUN}" stroke-width="1.6">${[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
    const rad = (a * Math.PI) / 180;
    const x1 = cx + Math.cos(rad) * (r + 1.8), y1 = cy + Math.sin(rad) * (r + 1.8);
    const x2 = cx + Math.cos(rad) * (r + 3.6), y2 = cy + Math.sin(rad) * (r + 3.6);
    return `<path d="M${x1.toFixed(1)} ${y1.toFixed(1)} L${x2.toFixed(1)} ${y2.toFixed(1)}"/>`;
  }).join('')}</g>`;

const cloud = (dy = 0) =>
  `<path transform="translate(0 ${dy})" d="M7 17.5 H17 A3.6 3.6 0 0 0 16.4 10.4 A5 5 0 0 0 6.9 11.6 A3 3 0 0 0 7 17.5 Z" fill="#ffffff" stroke="${CLOUD}" stroke-width="1.6"/>`;

const drops = (n: number, color = WATER) =>
  `<g stroke="${color}" stroke-width="1.7">${[8.5, 12, 15.5].slice(0, n).map((x) => `<path d="M${x} 19 L${x - 1} 21.5"/>`).join('')}</g>`;

const flakes = () =>
  `<g stroke="${ICE}" stroke-width="1.4">${[8.5, 12.5, 16.5].map((x, i) => {
    const y = i === 1 ? 21 : 20;
    return `<path d="M${x - 1.3} ${y} H${x + 1.3} M${x} ${y - 1.3} V${y + 1.3}"/>`;
  }).join('')}</g>`;

const INNER: Record<WeatherCondition, string> = {
  Unknown: `<path d="M9.5 9.5 A2.5 2.5 0 1 1 12 12 V13.5 M12 16.5 V16.6" stroke="${CLOUD}" stroke-width="1.8"/>`,
  Clear: sun(12, 12, 4.2),
  PartlyCloudy: sun(9, 8.5, 3) + cloud(2),
  Cloudy: cloud(0.5),
  Fog: `<g stroke="${CLOUD}" stroke-width="1.8"><path d="M5 9 H19 M4 12.5 H17 M6 16 H19 M8 19.5 H16"/></g>`,
  Drizzle: cloud(-2) + drops(2),
  Rain: cloud(-2) + drops(3),
  FreezingRain: cloud(-2) + drops(2, WATER) + `<path d="M15.5 19.2 H18 M16.75 18 V20.4" stroke="${ICE}" stroke-width="1.4"/>`,
  Showers: sun(8, 6.5, 2.4) + cloud(-1.5) + drops(2),
  Snow: cloud(-2) + flakes(),
  Thunderstorm: cloud(-2) + `<path d="M12.5 15 L10 19 H13 L11 23" stroke="${SUN}" stroke-width="1.8" fill="none"/>`,
};

export const WEATHER_LABEL: Record<WeatherCondition, string> = {
  Unknown: 'unbekannt',
  Clear: 'klar',
  PartlyCloudy: 'leicht bewölkt',
  Cloudy: 'bewölkt',
  Fog: 'Nebel',
  Drizzle: 'Niesel',
  Rain: 'Regen',
  FreezingRain: 'gefrierender Regen',
  Showers: 'Schauer',
  Snow: 'Schnee',
  Thunderstorm: 'Gewitter',
};

/** Symbol als SVG-Text, mit weissem Kreis dahinter. */
export function weatherSvg(cond: WeatherCondition, size = 28): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none"`
    + ` stroke-linecap="round" stroke-linejoin="round">`
    + `<circle cx="12" cy="12" r="11.3" fill="#ffffff" stroke="#c9d0d8" stroke-width="0.8"/>`
    + `${INNER[cond] ?? INNER.Unknown}</svg>`;
}

/** Alle Symbole als Kartenbilder "wx-<Zustand>" anmelden (fuer Symbol-Ebenen). */
export async function addWeatherImages(map: { hasImage(id: string): boolean; addImage(id: string, img: HTMLImageElement, opts?: { pixelRatio?: number }): void }): Promise<void> {
  const ratio = 2;
  await Promise.all((Object.keys(INNER) as WeatherCondition[]).map((cond) => new Promise<void>((resolve) => {
    const id = `wx-${cond}`;
    if (map.hasImage(id)) { resolve(); return; }
    const img = new Image(28 * ratio, 28 * ratio);
    img.onload = () => {
      if (!map.hasImage(id)) map.addImage(id, img, { pixelRatio: ratio });
      resolve();
    };
    img.onerror = () => resolve();
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(weatherSvg(cond, 28 * ratio))}`;
  })));
}

/** Temperaturfarben: kalt blau, um 5 Grad neutral, warm bernstein. Bewusst
 *  ohne Rot, das bleibt den Alarmen. */
export const TEMP_STOPS: [number, string][] = [
  [-15, '#2c3f9e'], [-8, '#3f6fd6'], [-3, '#6fa3ec'], [0, '#a8cbf2'],
  [5, '#e3e6ea'], [12, '#f4d29a'], [20, '#eaa44f'], [28, '#c96f1c'],
];

export function formatTemp(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '?';
  const r = Math.round(v);
  return `${r < 0 ? '−' : ''}${Math.abs(r)}°`;
}
